/**
 * src/components/dashboard/BalanceCard.tsx
 * Ultra-Premium Apple Wallet / Apple Card design with titanium sheen,
 * contactless EMV chip, adaptive Dark/Light styling, and tactile haptic feedback.
 */

import React from 'react';
import { 
  ArrowUpRight, 
  ArrowDownLeft, 
  CheckCircle2, 
  Plus, 
  Split, 
  FileText, 
  BarChart3, 
  Radio
} from 'lucide-react';
import { haptic, playTapSound } from '../../utils/audioHaptics';

interface BalanceCardProps {
  balance: number;
  totalReceived?: number;
  totalPaid?: number;
  role: 'admin' | 'user';
  username: string;
  onNewEntryClick?: () => void;
  onViewHistoryClick?: () => void;
  onSplitClick?: () => void;
  onAnalyticsClick?: () => void;
  onSettleClick?: () => void;
}

export const BalanceCard: React.FC<BalanceCardProps> = ({
  balance,
  totalReceived = 0,
  totalPaid = 0,
  role,
  username,
  onNewEntryClick,
  onViewHistoryClick,
  onSplitClick,
  onAnalyticsClick,
  onSettleClick
}) => {
  const isPositive = balance > 0;
  const isNegative = balance < 0;

  // Format currency with Indian grouping
  const formatAmount = (val: number) => {
    return Math.abs(val).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Status configuration
  let statusBadge = {
    label: 'All Settled',
    color: 'var(--ios-teal)',
    bg: 'rgba(100, 210, 255, 0.15)',
    icon: <CheckCircle2 size={13} strokeWidth={2.5} />
  };

  if (isPositive) {
    statusBadge = {
      label: role === 'admin' ? 'Net Surplus' : 'You Will Receive',
      color: 'var(--ios-green)',
      bg: 'rgba(48, 209, 88, 0.16)',
      icon: <ArrowUpRight size={13} strokeWidth={2.5} />
    };
  } else if (isNegative) {
    statusBadge = {
      label: role === 'admin' ? 'Net Deficit' : 'You Need To Pay',
      color: 'var(--ios-red)',
      bg: 'rgba(255, 69, 58, 0.16)',
      icon: <ArrowDownLeft size={13} strokeWidth={2.5} />
    };
  }

  const handleAction = (callback?: () => void) => {
    haptic.tap();
    playTapSound();
    if (callback) callback();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* 1. The Iconic Apple Card */}
      <div
        className="apple-card"
        style={{
          padding: '28px 24px',
          minHeight: '220px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          cursor: 'pointer',
          userSelect: 'none'
        }}
        onClick={() => handleAction(onViewHistoryClick)}
      >
        {/* Top Header: EMV Chip, NFC Contactless, Card Name & Apple Status Pill */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Realistic Golden EMV Chip */}
            <div className="apple-emv-chip" title="CKAudit Security Chip" />

            {/* Contactless Waves Icon */}
            <div style={{ opacity: 0.65, display: 'flex', alignItems: 'center' }} title="Contactless NFC">
              <Radio size={18} strokeWidth={2.2} />
            </div>

            <div>
              <div className="apple-card-title">
                CKAudit Titanium
              </div>
              <div className="apple-card-subtitle">
                {role === 'admin' ? 'Executive Master' : 'Member Account'}
              </div>
            </div>
          </div>

          {/* Status Capsule & Quick Settle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {onSettleClick && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAction(onSettleClick);
                }}
                className="ios-tap-target"
                style={{
                  padding: '5px 12px',
                  borderRadius: 'var(--radius-pill)',
                  background: 'var(--text-primary)',
                  color: 'var(--bg-primary)',
                  border: 'none',
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)'
                }}
              >
                <span>Settle</span>
                <ArrowUpRight size={13} strokeWidth={2.5} />
              </button>
            )}

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              padding: '5px 12px',
              borderRadius: 'var(--radius-pill)',
              background: statusBadge.bg,
              color: statusBadge.color,
              fontSize: '0.75rem',
              fontWeight: 700,
              backdropFilter: 'blur(12px)',
              border: `0.5px solid ${statusBadge.color}40`,
              boxShadow: `0 2px 10px ${statusBadge.color}20`
            }}>
              {statusBadge.icon}
              <span>{statusBadge.label}</span>
            </div>
          </div>
        </div>

        {/* Center: Large Apple SF Pro Balance Number */}
        <div style={{ margin: '22px 0 16px' }}>
          <div className="apple-card-subtitle" style={{ marginBottom: '6px' }}>
            {role === 'admin' ? 'Total Ledger Position' : 'Account Balance'}
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '6px'
          }}>
            <span className="apple-card-currency">
              ₹
            </span>
            <span className={`apple-card-balance ${isPositive ? 'positive' : isNegative ? 'negative' : ''}`}>
              {isNegative ? `-${formatAmount(balance)}` : formatAmount(balance)}
            </span>
          </div>
        </div>

        {/* Card Footer: Cardholder Name & Mini Totals */}
        <div 
          className="apple-card-divider"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            paddingTop: '16px'
          }}
        >
          <div>
            <div className="apple-card-subtitle" style={{ fontSize: '0.625rem', letterSpacing: '0.12em' }}>
              Cardholder
            </div>
            <div className="apple-card-holder-name">
              {username}
            </div>
          </div>

          {/* Subtotals in discrete Apple frosted capsules */}
          <div style={{ display: 'flex', gap: '8px', textAlign: 'right' }}>
            <div className="apple-subtotal-pill">
              <div style={{ fontSize: '0.72rem', color: 'var(--ios-green)', fontWeight: 700 }}>
                +₹{formatAmount(totalReceived)}
              </div>
              <div className="apple-card-subtitle" style={{ fontSize: '0.5625rem' }}>
                {role === 'admin' ? 'Received' : 'Paid by Me'}
              </div>
            </div>

            <div className="apple-subtotal-pill">
              <div style={{ fontSize: '0.72rem', color: 'var(--ios-red)', fontWeight: 700 }}>
                -₹{formatAmount(totalPaid)}
              </div>
              <div className="apple-card-subtitle" style={{ fontSize: '0.5625rem' }}>
                {role === 'admin' ? 'Paid' : 'Paid by Them'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Apple Pay Action Buttons Dock */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '12px',
        padding: '4px 2px'
      }}>
        {/* Action 1: New Entry / Pay */}
        <button
          type="button"
          onClick={() => handleAction(onNewEntryClick)}
          className="apple-pay-button ios-tap-target"
        >
          <div className="apple-pay-icon-circle" style={{ background: 'linear-gradient(135deg, #0a84ff 0%, #0056b3 100%)', borderColor: 'rgba(10, 132, 255, 0.4)' }}>
            <Plus size={22} strokeWidth={2.5} color="#ffffff" />
          </div>
          <span className="apple-pay-label">
            {role === 'admin' ? 'Add Entry' : 'Submit'}
          </span>
        </button>

        {/* Action 2: Split Bill */}
        <button
          type="button"
          onClick={() => handleAction(onSplitClick)}
          className="apple-pay-button ios-tap-target"
        >
          <div className="apple-pay-icon-circle">
            <Split size={20} strokeWidth={2.2} color="var(--ios-teal)" />
          </div>
          <span className="apple-pay-label">Split Bill</span>
        </button>

        {/* Action 3: Analytics */}
        <button
          type="button"
          onClick={() => handleAction(onAnalyticsClick)}
          className="apple-pay-button ios-tap-target"
        >
          <div className="apple-pay-icon-circle">
            <BarChart3 size={20} strokeWidth={2.2} color="var(--ios-green)" />
          </div>
          <span className="apple-pay-label">Analytics</span>
        </button>

        {/* Action 4: Statement */}
        <button
          type="button"
          onClick={() => handleAction(onViewHistoryClick)}
          className="apple-pay-button ios-tap-target"
        >
          <div className="apple-pay-icon-circle">
            <FileText size={20} strokeWidth={2.2} color="var(--ios-orange)" />
          </div>
          <span className="apple-pay-label">Ledger</span>
        </button>
      </div>
    </div>
  );
};
